import PropTypes from 'prop-types';

export const permissionPropType = PropTypes.shape({
  fieldName: PropTypes.string.isRequired,
  requires: PropTypes.arrayOf(PropTypes.string),
  requiredFor: PropTypes.arrayOf(PropTypes.string),
  tooltip: PropTypes.string,
});

export const permissionRowPropType = PropTypes.shape({
  rowId: PropTypes.string.isRequired,
  rowTitle: PropTypes.string.isRequired,
  hide: PropTypes.bool,
  permissions: PropTypes.shape({
    view: permissionPropType,
    addEdit: permissionPropType,
    book: permissionPropType,
    modify: permissionPropType,
    history: permissionPropType,
    cancel: permissionPropType,
    blockTime: permissionPropType,
  }).isRequired,
});

export const permissionSectionPropType = PropTypes.shape({
  sectionId: PropTypes.string.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  showColumns: PropTypes.arrayOf(PropTypes.oneOf(
    ['view', 'addEdit', 'book', 'modify', 'history', 'cancel', 'blockTime'])
  ),
  rows: PropTypes.arrayOf(permissionRowPropType).isRequired,
  hide: PropTypes.bool,
});

export const permissionsDataPropType = PropTypes.arrayOf(permissionPropType);

export default ({
  canSeeEngagePermissions,
  canSeeAutoPayoutPermissions,
}) => [
  {
    sectionId: 'appointmentsManagement',
    sectionTitle: 'Appointments Management',
    showColumns: ['view', 'addEdit', 'book', 'modify', 'history', 'cancel', 'blockTime'],
    rows: [
      {
        rowId: 'appointments',
        rowTitle: 'Appointments',
        permissions: {
          view: { fieldName: 'permissions.appointments.view' },
          book: { fieldName: 'permissions.appointments.book' },
          modify: { fieldName: 'permissions.appointments.modify' },
          history: { fieldName: 'permissions.appointments.history' },
          cancel: { fieldName: 'permissions.appointments.cancel' },
          blockTime: { fieldName: 'permissions.blockTime.modify' },
        },
      },
      {
        rowId: 'appointmentsViewContactInfo',
        rowTitle: 'Form Show Contact Info',
        permissions: {
          view: { fieldName: 'permissions.appointments.show_contact_info' },
        },
      },
    ],
  },
  {
    sectionId: 'clientManagement',
    sectionTitle: 'Client Management',
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'clients',
        rowTitle: 'Clients',
        permissions: {
          view: { fieldName: 'permissions.clients.view' },
          addEdit: { fieldName: 'permissions.clients.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'staffManagement',
    sectionTitle: 'Staff Management',
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'staff',
        rowTitle: 'Staff',
        permissions: {
          view: {
            fieldName: 'permissions.staffManagement.view',
            // eslint-disable-next-line max-len
            tooltip: 'Staff Management: View permission allows a brand or shop user to view the list of brand and shop users in this brand.',
          },
          addEdit: {
            fieldName: 'permissions.staffManagement.modify',
            // eslint-disable-next-line max-len
            tooltip: 'Staff Management: Add/Edit permission allows a brand user to add, edit, and delete other brand and shop users in this brand. Shop users can only add, edit, or delete other shop users.',
          },
        },
      },
      {
        rowId: 'barbers',
        rowTitle: 'Barbers',
        permissions: {
          view: { fieldName: 'permissions.barbers.view' },
          addEdit: { fieldName: 'permissions.barbers.modify' },
        },
      },
      {
        rowId: 'barbersProfile',
        rowTitle: 'Barbers Profile',
        permissions: {
          view: { fieldName: 'permissions.profile.view' },
          addEdit: { fieldName: 'permissions.profile.modify' },
        },
      },
      {
        rowId: 'barbersSettings',
        rowTitle: 'Barbers Settings',
        permissions: {
          view: { fieldName: 'permissions.barberSettings.view' },
          addEdit: { fieldName: 'permissions.barberSettings.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'servicesManagement',
    sectionTitle: 'Services Management',
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'services',
        rowTitle: 'Services',
        permissions: {
          view: { fieldName: 'permissions.services.view' },
          addEdit: { fieldName: 'permissions.services.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'paymentSettings',
    sectionTitle: 'Payment Settings',
    showColumns: ['view', 'addEdit'],
    hide: !canSeeAutoPayoutPermissions,
    rows: [
      {
        rowId: 'autoPayout',
        rowTitle: 'Auto Payout',
        permissions: {
          view: {
            fieldName: 'permissions.autoPayout.view',
            requiredFor: ['permissions.autoPayout.modify'],
            tooltip: 'This permission grants this user the ability to view Auto Payout settings.',
          },
          addEdit: {
            fieldName: 'permissions.autoPayout.modify',
            requires: ['permissions.autoPayout.view'],
            tooltip: 'This permission grants this user the ability to edit Auto Payout settings.',
          },
        },
      },
    ],
  },
  {
    sectionId: 'productsManagement',
    sectionTitle: 'Products & Inventory Management',
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'products',
        rowTitle: 'Products',
        permissions: {
          view: {
            fieldName: 'permissions.products.view',
            requiredFor: ['permissions.products.modify', 'permissions.inventory.modify'],
          },
          addEdit: {
            fieldName: 'permissions.products.modify',
            requires: ['permissions.products.view'],
          },
        },
      },
      {
        rowId: 'inventory',
        rowTitle: 'Inventory',
        permissions: {
          addEdit: {
            fieldName: 'permissions.inventory.modify',
            requires: ['permissions.products.view'],
            // eslint-disable-next-line max-len
            tooltip: 'This permission grants this user the ability to use the Stock Taking, New Inventory and Low Inventory Report',
          },
        },
      },
    ],
  },
  {
    sectionId: 'shopSettings',
    sectionTitle: 'Shop Settings',
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'shopDetails',
        rowTitle: 'Shop Details',
        permissions: {
          view: { fieldName: 'permissions.settings.view' },
          addEdit: { fieldName: 'permissions.settings.modify' },
        },
      },
      {
        rowId: 'bankAccount',
        rowTitle: 'Bank Account',
        permissions: {
          view: { fieldName: 'permissions.bankAccount.view' },
          addEdit: { fieldName: 'permissions.bankAccount.modify' },
        },
      },
      {
        rowId: 'taxes',
        rowTitle: 'Taxes',
        permissions: {
          view: { fieldName: 'permissions.taxes.view' },
          addEdit: { fieldName: 'permissions.taxes.modify' },
        },
      },
      {
        rowId: 'waitingList',
        rowTitle: 'Waiting List',
        permissions: {
          view: { fieldName: 'permissions.waitingList.view' },
          addEdit: { fieldName: 'permissions.waitingList.modify' },
        },
      },
    ],
  },
  {
    sectionId: 'reportingManagement',
    sectionTitle: 'Reporting Management',
    showColumns: ['view'],
    rows: [
      {
        rowId: 'reports',
        rowTitle: 'Reports',
        permissions: {
          view: { fieldName: 'permissions.reports.view' },
        },
      },
      {
        rowId: 'timeReports',
        rowTitle: 'Time Reports',
        permissions: {
          view: { fieldName: 'permissions.timeTrack.view' },
        },
      },
      {
        rowId: 'dashboard',
        rowTitle: 'Dashboard',
        permissions: {
          view: { fieldName: 'permissions.dashboard.view' },
        },
      },
      {
        rowId: 'analytics',
        rowTitle: 'Analytics',
        permissions: {
          view: { fieldName: 'permissions.analytics.view' },
        },
      },
    ],
  },
  {
    sectionId: 'marketingManagement',
    sectionTitle: 'Marketing Management',
    showColumns: ['view', 'addEdit'],
    rows: [
      {
        rowId: 'discounts',
        rowTitle: 'Discounts',
        permissions: {
          view: { fieldName: 'permissions.discounts.view' },
          addEdit: { fieldName: 'permissions.discounts.modify' },
        },
      },
      {
        rowId: 'promos',
        rowTitle: 'Promos',
        permissions: {
          view: { fieldName: 'permissions.promos.view' },
          addEdit: { fieldName: 'permissions.promos.modify' },
        },
      },
      {
        rowId: 'engage',
        rowTitle: 'Engage',
        hide: !canSeeEngagePermissions,
        permissions: {
          view: { fieldName: 'permissions.engage.view' },
        },
      },
    ],
  },
  {
    sectionId: 'register',
    sectionTitle: 'Register',
    showColumns: ['view'],
    rows: [
      {
        rowId: 'register',
        rowTitle: 'Register',
        permissions: {
          view: { fieldName: 'permissions.register.view' },
        },
      },
      {
        rowId: 'cashRegister',
        rowTitle: 'Cash Register',
        permissions: {
          view: { fieldName: 'permissions.cashRegister.view' },
        },
      },
      {
        rowId: 'openCashDrawer',
        rowTitle: 'Open Cash Drawer',
        permissions: {
          view: { fieldName: 'permissions.cashRegister.modify' },
        },
      },
      {
        rowId: 'customCharge',
        rowTitle: 'Custom Charge',
        permissions: {
          view: { fieldName: 'permissions.customCharge.view' },
        },
      },
      {
        rowId: 'dailySummary',
        rowTitle: 'Daily Summary',
        permissions: {
          view: { fieldName: 'permissions.dailySummary.view' },
        },
      },
      {
        rowId: 'dailySummaryHistory',
        rowTitle: 'Daily Summary History',
        permissions: {
          view: { fieldName: 'permissions.dailySummaryHistory.view' },
        },
      },
    ],
  },
];
